import i18n from '@/i18n';
import { displayShopFact } from '@/i18n/catalogDisplayText';
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import {
  buildStoreBasicRows,
  getStoreDisplayName,
  pickStoreInfoNode,
} from '@/tiktok-platform/utils/tiktokStoreInfo';
import ShopSiteListPanel from '@/tiktok-platform/modules/shop/ShopSiteListPanel';
import ShopCategoryPanel from '@/tiktok-platform/modules/shop/ShopCategoryPanel';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import TiktokRefreshButton from '@/tiktok-platform/components/TiktokRefreshButton';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import TiktokShopAvatar from '@/tiktok-platform/components/TiktokShopAvatar';
import { SyncHintMark } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokManualSyncQuota } from '@/tiktok-platform/hooks/useTiktokManualSyncQuota';
import { invalidateShopCategories, invalidateShopSiteList } from '@/tiktok-platform/utils/tiktokTemplateScopeCache';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { imageStorageAPI } from '@/utils/api';
import { pickUrlFromImageStorageResponse } from '@/utils/imageUrlUtils';
import { authLabel } from '@/pages/ipe/workbench/utils/platformShopManageUtils';
import '@/styles/tiktok-platform/tiktok-product-ops.css';
import '@/styles/tiktok-platform/tiktok-ops-list-toolbar.css';

const SHOP_PROFILE_SYNC_KINDS = ['shopProfile'];
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/**
 * 同步更新打平台接口与页面展示对应关系：
 * - GET /authorization/202309/shops → 基本信息（名称 / 店铺 ID / cipher / region / seller_type）
 * - GET /seller/202309/shops → 售卖站点（经营 region）
 * - GET /seller/202309/permissions → 权限快照（入库，本页不单独展示）
 * - GET /product/202309/categories → 可发类目（is_leaf=true 且 AVAILABLE）
 * 品牌已迁至「模板库 · 品牌库」，本页不再同步/展示。
 */
const SYNC_SECTION_LABELS = {
  authorizedShops: '基本信息',
  activeShops: '售卖站点',
  permissions: '卖家权限',
  categories: '可发类目',
};

const SYNC_HINT_TEXT = [
  '从 TikTok 开放平台拉取并更新本地：',
  '· Get Authorized Shops → 基本信息（店铺名 / 店铺 ID / Cipher / 类型 / 授权）',
  '· Get Active Shops → 售卖站点（经营 region）',
  '· Get Seller Permissions → 权限快照',
  '· Get Categories → 可发类目（is_leaf + AVAILABLE）',
  '品牌请到「模板库 · 品牌库」单独同步。',
].join('\n');

/** 将平台 7000020 等 token 错误转成可操作提示 */
function formatTiktokShopSyncError(raw) {
  const msg = String(raw || '').trim();
  if (!msg) return '';
  const lower = msg.toLowerCase();
  if (lower.includes('7000007') || lower.includes('access_token is expired') || (lower.includes('access_token') && lower.includes('expired'))) {
    return fixedUi('access_token 已过期（7000007）。请到 TikTok 卖家中心重新授权，并在平台管理更新店铺 Token；「授权过期时间」可作为是否过期的依据。');
  }
  if (lower.includes('7000020') || lower.includes('access_token invalid')) {
    return fixedUi('access_token 无效（7000020）。常见原因：token 填错、已失效/被撤销、或未用卖家中心重新授权。请到 TikTok 卖家中心重新授权并更新店铺密钥后再同步。');
  }
  if (lower.includes('7000019') || lower.includes("don't have this api access") || lower.includes('authorize this api')) {
    return fixedUi('当前 access_token 未授权该接口。请到 TikTok 卖家中心勾选对应 API 权限并重新授权。');
  }
  return msg;
}

function KvTable({ rows }) {
  if (!rows.length) {
    return <p className="tko-return-detail__empty">{fixedUi("暂无数据")}</p>;
  }
  return (
    <table className="tko-return-detail__kv">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key || row.label}>
            <th scope="row">{displayShopFact(row.label)}</th>
            <td>{displayShopFact(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ShopInfoPage() {
  const {
    activeShop,
    loading,
    activeShopId,
    scopedShops = [],
    isAllShopsMode,
    patchLocalShop,
  } = useTiktokShop();
  const { runBlocking, isBusy } = useTiktokSyncJobs();
  const [pageShopScope, setPageShopScope] = useState(() => activeShopId || '');
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [fetchError, setFetchError] = useState('');
  const [siteList, setSiteList] = useState(null);
  const [siteListLoading, setSiteListLoading] = useState(false);
  const [siteListError, setSiteListError] = useState('');
  const [categories, setCategories] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState('');
  const prevShopIdRef = useRef(null);
  const pendingSyncRef = useRef(false);

  useEffect(() => {
    setPageShopScope(activeShopId || '');
  }, [activeShopId]);

  const showOverview = isAllShopsScope(pageShopScope)
    || (isAllShopsMode && !isConcreteShopId(pageShopScope));

  const viewShopId = useMemo(() => {
    if (isConcreteShopId(pageShopScope)) return String(pageShopScope);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    return '';
  }, [pageShopScope, activeShopId]);

  const viewShop = useMemo(() => {
    if (!viewShopId) return null;
    return (scopedShops || []).find((s) => String(s.id) === String(viewShopId))
      || (activeShop && String(activeShop.id) === String(viewShopId) ? activeShop : null);
  }, [viewShopId, scopedShops, activeShop]);

  const loadFromCache = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      setStoreInfo(null);
      setSiteList(null);
      setCategories(null);
      return;
    }
    // 进页只读 store-info 本地缓存（含基本信息 / 站点 / 可发类目），不打平台
    setFetching(true);
    setSiteListLoading(true);
    setCategoryLoading(true);
    setFetchError('');
    setSiteListError('');
    setCategoryError('');
    try {
      const res = await tiktokPlatformAPI.getShopStoreInfo(viewShopId);
      setStoreInfo(res?.storeInfo ?? null);
      setSiteList(res?.siteList ?? null);
      const list = res?.categories;
      setCategories(Array.isArray(list) ? list : []);
      setLastRefreshedAt(res?.syncedAt || '');
    } catch (e) {
      setStoreInfo(null);
      setSiteList(null);
      setCategories([]);
      setFetchError(e?.message || fixedUi('读取店铺信息缓存失败'));
      setLastRefreshedAt('');
    } finally {
      setFetching(false);
      setSiteListLoading(false);
      setCategoryLoading(false);
    }
  }, [viewShopId]);

  const applyProfileSyncResponse = useCallback((res) => {
    if (!res) return {};
    const syncedAt = res.syncedAt || res.profileSyncedAt || '';
    const errors = res.errors && typeof res.errors === 'object' ? res.errors : {};
    if (res.storeInfo != null) setStoreInfo(res.storeInfo);
    if (res.siteList != null) setSiteList(res.siteList);
    const list = res?.categories;
    if (Array.isArray(list)) {
      setCategories(list);
    }
    setFetchError(formatTiktokShopSyncError(errors.authorizedShops || ''));
    setSiteListError(formatTiktokShopSyncError(errors.activeShops || ''));
    setCategoryError(formatTiktokShopSyncError(errors.categories || ''));
    if (syncedAt) setLastRefreshedAt(syncedAt);
    return errors;
  }, []);

  const syncFromTiktokAction = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      return;
    }
    setFetching(true);
    setSiteListLoading(true);
    setCategoryLoading(true);
    setFetchError('');
    setSiteListError('');
    setCategoryError('');
    try {
      await runBlocking({
        kind: 'shopProfile',
        shopId: viewShopId,
        get label() { return fixedUi("同步店铺信息"); },
        syncFn: async ({ signal } = {}) => {
          // 仅「同步更新」打平台；返回体已含本地可展示字段
          const res = await tiktokPlatformAPI.syncShopProfile(viewShopId, { signal });
          const errors = applyProfileSyncResponse(res) || {};
          // 店铺类目已刷新：清模板库类目缓存，下次进页拿新树
          if (!errors.categories) {
            invalidateShopCategories(viewShopId);
          }
          if (!errors.authorizedShops && !errors.activeShops) {
            invalidateShopSiteList(viewShopId);
          }
          return { res, errors };
        },
        buildSuccess: (result) => {
          const errors = result?.errors && typeof result.errors === 'object' ? result.errors : {};
          const failedKeys = Object.keys(errors).filter((k) => errors[k]);
          if (failedKeys.length > 0) {
            const firstMsg = formatTiktokShopSyncError(errors[failedKeys[0]]);
            const labels = failedKeys
              .map((k) => (SYNC_SECTION_LABELS[k] ? fixedUi(SYNC_SECTION_LABELS[k]) : k))
              .join(fixedUi('、'));
            return {
              imported: Math.max(0, 4 - failedKeys.length),
              toastType: 'warning',
              message: failedKeys.length === 1
                ? firstMsg
                : fixedUiTpl('部分同步失败（{0}）：{1}', labels, firstMsg),
            };
          }
          return {
            imported: 4,
            message: result?.res?.message || fixedUi("店铺信息同步成功"),
          };
        },
      });
    } catch (e) {
      try {
        await loadFromCache();
      } catch (_) {
        /* ignore */
      }
      if (e?.code !== 'SYNC_IN_PROGRESS' && e?.message) {
        showToast(e.message, 'error');
      }
    } finally {
      setFetching(false);
      setSiteListLoading(false);
      setCategoryLoading(false);
    }
  }, [viewShopId, applyProfileSyncResponse, loadFromCache, runBlocking]);

  const {
    refresh: handleRefreshAll,
    refreshing: syncingLocal,
    cooldownSeconds: refreshAllCooldown,
  } = useGuardedRefresh(syncFromTiktokAction);

  const quota = useTiktokManualSyncQuota('shop-profile');
  const handleRefreshClick = () => {
    if (!quota.assertCanSync()) return;
    handleRefreshAll();
  };

  const handleAvatarPick = () => {
    if (uploadingAvatar || !viewShopId) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !viewShopId) return;
    if (!file.type.startsWith('image/')) {
      showToast(fixedUi("请选择图片文件"), 'warning');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      showToast(fixedUi("头像图片不能超过 5MB"), 'warning');
      return;
    }
    setUploadingAvatar(true);
    try {
      const uploaded = await imageStorageAPI.save(file, { folder: 'image-space', registerImageSpace: false });
      const url = pickUrlFromImageStorageResponse(uploaded);
      if (!url) throw new Error(fixedUi('上传失败'));
      await tiktokPlatformAPI.patchShopAvatar(viewShopId, url);
      patchLocalShop?.(viewShopId, { avatarUrl: url });
      showToast(fixedUi("店铺头像已更新"), 'success');
    } catch (err) {
      showToast(err?.message || fixedUi('头像上传失败'), 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    if (showOverview || !viewShopId) {
      prevShopIdRef.current = null;
      if (!viewShopId) setLastRefreshedAt('');
      return;
    }

    const prevShopId = prevShopIdRef.current;
    prevShopIdRef.current = viewShopId;
    const shouldSync = pendingSyncRef.current;
    pendingSyncRef.current = false;

    if (shouldSync) {
      syncFromTiktokAction();
      return;
    }

    if (prevShopId == null || String(prevShopId) !== String(viewShopId)) {
      loadFromCache();
    }
  }, [showOverview, viewShopId, loadFromCache, syncFromTiktokAction]);

  const syncBusy = isBusy('shopProfile', viewShopId);
  const isRefreshing = syncingLocal || syncBusy;

  const shopFallbackName = viewShop?.shopName || '';
  const displayName = useMemo(
    () => getStoreDisplayName(storeInfo, shopFallbackName),
    [storeInfo, shopFallbackName],
  );
  const basicRows = useMemo(() => buildStoreBasicRows(storeInfo), [storeInfo]);
  const hasBasicInfo = Boolean(pickStoreInfoNode(storeInfo));

  const syncedMetaText = lastRefreshedAt
    ? `${fixedUi('最近同步')}：${formatSiteListSyncedAt(lastRefreshedAt)}`
    : (isRefreshing ? fixedUi('正在同步…') : fixedUi('尚未同步'));

  const shopDimensionTabs = isAllShopsMode && scopedShops.length >= 2 ? (
    <PlatformShopDimensionTabs
      shops={scopedShops}
      value={pageShopScope || PLATFORM_ALL_SHOPS_ID}
      onChange={setPageShopScope}
      showAll
    />
  ) : null;

  if (loading) {
    return (
      <div className="tko-return-detail tko-shop-info" data-testid="tiktok-shop-info">
        <div className="tko-return-detail__loading">{fixedUi("加载店铺信息…")}</div>
      </div>
    );
  }

  if (showOverview) {
    return (
      <div className="tko-return-detail tko-shop-info" data-testid="tiktok-shop-info">
        <header className="tko-return-detail__header">
          <div className="tko-return-detail__title-block">
            <h1 className="tko-return-detail__title">{fixedUi("店铺信息")}</h1>
          </div>
          <div className="tko-return-detail__actions">
            <TiktokRefreshButton
              onClick={() => setShopPickOpen(true)}
              size="sm"
              className="tko-btn tko-btn--primary tko-btn--sm"
            >
              {fixedUi("同步店铺信息")}
            </TiktokRefreshButton>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="tko-shop-info__overview">
          <div className="tko-shop-info__table-wrap">
            <table className="tko-shop-info__table tko-shop-info__overview-table">
              <thead>
                <tr>
                  <th scope="col">{fixedUi("店铺名称")}</th>
                  <th scope="col">{fixedUi("店铺 ID")}</th>
                  <th scope="col">{fixedUi("授权状态")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {(scopedShops || []).map((shop) => (
                  <tr key={shop.id}>
                    <td>{shop.shopName || fixedUiTpl('店铺 {0}', shop.id)}</td>
                    <td>{shop.id}</td>
                    <td>{authLabel(shop.authStatus, shop.mockBinding)}</td>
                    <td>
                      <button
                        type="button"
                        className="tko-btn tko-btn--ghost tko-btn--sm"
                        onClick={() => setPageShopScope(String(shop.id))}
                      >
                        {fixedUi('查看详情')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <PlatformShopPickModal
          open={shopPickOpen}
          shops={scopedShops}
          title={fixedUi("选择要同步的店铺")}
          description={fixedUi("全部视图下请选择一家店铺后再同步店铺信息。")}
          confirmText={fixedUi('查看并同步')}
          onClose={() => setShopPickOpen(false)}
          onConfirm={(shopId) => {
            setShopPickOpen(false);
            pendingSyncRef.current = true;
            setPageShopScope(String(shopId));
          }}
        />
      </div>
    );
  }

  if (!viewShop) {
    return (
      <div className="tko-return-detail tko-shop-info" data-testid="tiktok-shop-info">
        <header className="tko-return-detail__header">
          <div className="tko-return-detail__title-block">
            <h1 className="tko-return-detail__title">{fixedUi("店铺信息")}</h1>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="tko-return-detail__loading">
          {fixedUi("尚未配置 TikTok 店铺。")}
          {' '}
          <Link to={`${IPE_WORKBENCH_PATH}?activeNav=platformShops`} className="tko-return-detail__crumb-link">
            {fixedUi("去平台管理 · 店铺管理")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <TiktokSyncProgressSlot kinds={SHOP_PROFILE_SYNC_KINDS} />
      <div className="tko-return-detail tko-shop-info" data-testid="tiktok-shop-info">
        {shopDimensionTabs}
        <header className="tko-return-detail__header">
          <div className="tko-shop-info__identity">
            <button
              type="button"
              className="tko-shop-info__avatar-btn"
              onClick={handleAvatarPick}
              disabled={uploadingAvatar}
              aria-label={fixedUi("更换店铺头像")}
              title={fixedUi("点击上传自定义头像")}
            >
              <TiktokShopAvatar
                className="tko-shop-info__avatar"
                avatarUrl={viewShop?.avatarUrl}
                size={72}
              />
              <span className="tko-shop-info__avatar-overlay" aria-hidden="true">
                {uploadingAvatar ? fixedUi("上传中") : fixedUi("更换")}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="tko-shop-info__avatar-input"
              onChange={handleAvatarChange}
            />
            <div className="tko-return-detail__title-block">
              <h1 className="tko-return-detail__title">{fixedUi("店铺信息")}</h1>
              <div className="tko-return-detail__meta">
                <strong className="tko-return-detail__no">{displayName}</strong>
              </div>
              <p className="tko-shop-info__avatar-tip">
                {fixedUi("点击头像可自定义；未设置时顶栏默认显示 TikTok")}
              </p>
            </div>
          </div>
          <div className="tko-return-detail__actions">
            <div className="tko-ops-list-toolbar__sync">
              <span className="tko-sync-updated-at" title={syncedMetaText}>
                {syncedMetaText}
              </span>
              {quota.remaining != null ? (
                <span
                  className="tko-sync-quota"
                  title={i18n.t('tiktokList.quotaTitle', { ns: 'workbench', limit: quota.limit })}
                >
                  {i18n.t('tiktokList.quotaRemain', { ns: 'workbench', remaining: quota.remaining, limit: quota.limit })}
                </span>
              ) : null}
              <span className="tko-ops-list-toolbar__sync-action tko-ops-list-toolbar__sync-action--with-hint">
                <SyncHintMark text={fixedUi(SYNC_HINT_TEXT)} />
                <TiktokRefreshButton
                  onClick={handleRefreshClick}
                  loading={isRefreshing}
                  cooldownSeconds={refreshAllCooldown}
                  busyLabel={fixedUi("同步中…")}
                  size="sm"
                  className="tko-btn tko-btn--secondary tko-btn--sm tko-ops-list-toolbar__sync-btn tko-ops-list-toolbar__sync-btn--with-hint"
                  disabled={quota.exhausted}
                >
                  {quota.exhausted ? i18n.t('tiktokList.quotaExhausted', { ns: 'workbench' }) : fixedUi('拉取 TikTok 后台数据')}
                </TiktokRefreshButton>
              </span>
            </div>
          </div>
        </header>

        {fetchError ? (
          <div className="tko-ops-error" role="alert">{fetchError}</div>
        ) : null}

        {fetching && !storeInfo && !syncBusy ? (
          <div className="tko-return-detail__loading">{fixedUi("正在查询店铺信息…")}</div>
        ) : null}

        <div className="tko-return-detail__body">
          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{fixedUi("基本信息")}</h2>
            {!fetching && !fetchError && !hasBasicInfo ? (
              <p className="tko-return-detail__empty">{fixedUi("暂无店铺基础信息，请点击「拉取 TikTok 后台数据」同步。")}</p>
            ) : (
              <KvTable rows={basicRows} />
            )}
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{fixedUi("售卖站点")}</h2>
            <ShopSiteListPanel
              loading={siteListLoading}
              error={siteListError}
              siteList={siteList}
            />
          </section>

          <section className="tko-return-detail__section tko-shop-info__section--categories">
            <ShopCategoryPanel
              loading={categoryLoading}
              error={categoryError}
              categories={categories}
            />
          </section>
        </div>
      </div>
    </>
  );
}
